import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle } from '../../../Components/UI/card';
import { TrendingUp, TrendingDown, CircleDollarSignIcon, Banknote } from 'lucide-react';
import { BsCartFill } from 'react-icons/bs';
import { useCurrency } from '../../../Hooks/useCurrency';
import TransactionDropdown from '../../../Modules/Connections/TransactionDropdown';
import TransactionEntry from '../../../Modules/Connections/Tabs/TransactionEntry';
import { useNavigate } from 'react-router-dom';
import { checkUserRights, formatAmount } from '../../../Utils/Utilities';
import useIsMobile from "../../../Hooks/useIsMobile";

const QuickStats = ({ quickStatsTxn, quickStatsOrders, showOrders = true, showPOs = true }) => {
    const currencySymbol = useCurrency();
    const [transactionTypeID, setTransactionTypeID] = useState(1);
    const [openTransactionEntry, setOpenTransactionEntry] = useState(false);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { data: agencyData } = useSelector(state => state.agencyData);
 
    const formatPercentage = (value) => {
        if (value === null || value === undefined || isNaN(value)) return "0.00%";
        return `${Number(value).toFixed(0)}%`;
    };

    const PercentageIndicator = ({ percentage }) => {
        if (percentage === 0) return;
        const isPositive = percentage >= 0;
        return (
            <div className={`flex items-center gap-1 text-xs sm:text-sm ${isPositive ? 'text-sahi-green' : 'text-sahi-red'}`}>
                {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{formatPercentage(percentage)}</span>
            </div>
        );
    };

    const getMobileFormatPrice = (price) => {
        if(isMobile) return formatAmount(price, agencyData.CountryID);
        return price
    }

    const DashboardCard = ({ title, icon: Icon, iconCol = "text-black", amount, percentage, bgColor = "bg-gray-100", xid }) => {
        const handleCardClick = () => {
            if (xid) {
                navigate(`/transactions?xid=${xid}`);
            }
        };
        return (
            <Card className={`transition-all duration-300 hover:shadow-lg ${bgColor} border border-gray-200 shadow-sm rounded-xl overflow-hidden !gap-1`}
                onClick={handleCardClick}
            >
                {/* Header with less bottom padding */}
                <CardHeader className="pt-2 sm:pt-4 lg:pt-6 pb-1 sm:pb-2 lg:pb-3 px-2 sm:px-4 lg:px-6">
                    <CardTitle className="flex flex-row justify-between items-center gap-1 sm:gap-2 text-sm sm:text-sm md:text-base">
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                            <Icon className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 ${iconCol} flex-shrink-0`} />
                            <span className="text-gray-700 truncate font-medium break-words">
                                {title}
                            </span>
                        </div>
                    </CardTitle>
                </CardHeader>

                {/* Content with less top padding */}
                <CardContent className="pt-1 sm:pt-2 lg:pt-3 pb-2 sm:pb-4 lg:pb-6 px-2 sm:px-4 lg:px-6">
                    <div className="flex flex-row justify-between items-center">
                        <div className="text-base sm:text-lg md:text-xl font-bold text-gray-900 break-words leading-tight">
                            <span className="text-sm sm:text-base md:text-lg font-normal text-gray-500 mr-1">
                                {currencySymbol}
                            </span>
                            {getMobileFormatPrice(amount)}
                        </div>
                        <div className="flex-shrink-0 ml-1 sm:ml-2">
                            <PercentageIndicator percentage={percentage} />
                        </div>
                    </div>
                </CardContent>
            </Card>
        )
    }


    return (
        <div className={`rounded-xl bg-sahi-white ${checkUserRights().canSeeCstrs == true ? 'mt-4' : ''}  p-4`}>
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <span className="text-text-color font-semibold text-xl">This Month Thus Far</span>
                    <TransactionDropdown
                        externalOpen={true}
                        minimalIcon={true}
                        className={`relative !h-6 !w-6`}
                        onTransactionTypeSelect={(value) => {
                            setTransactionTypeID(value);
                            setOpenTransactionEntry(true);
                        }}
                    />
                </div>

                {/* Responsive Grid Layout */}
                <div className="grid grid-cols-2 gap-3 bg-sahi-white  sahi-radius">
                    {/* Customer-related cards (Sales, Receipts, Ords) */}
                    {(checkUserRights().canSeeCstrs || (!checkUserRights().canSeeCstrs && !checkUserRights().canSeeSplrs)) && (
                        <>
                            {/* Sales Card */}
                            <DashboardCard
                                title="Sales"
                                icon={Banknote}
                                iconCol="text-sahi-green"
                                amount={quickStatsTxn?.salesInvoice?.amount}
                                percentage={quickStatsTxn?.salesInvoice?.percentageChange}
                                xid={"1"}
                            />

                            {/* Receipts Card */}
                            <DashboardCard
                                title="Receipts"
                                icon={CircleDollarSignIcon}
                                iconCol="text-sahi-green"
                                amount={quickStatsTxn?.receipt?.amount}
                                percentage={quickStatsTxn?.receipt?.percentageChange}
                                xid={"5"}
                            />

                            {showOrders && (
                                <DashboardCard
                                    title="Sales"
                                    icon={BsCartFill}
                                    iconCol="text-sahi-green"
                                    amount={quickStatsOrders?.salesOrder?.amount}
                                    percentage={quickStatsOrders?.salesOrder?.percentageChange}
                                />
                            )}
                        </>
                    )}

                    {/* Supplier-related cards (Purchases, Payments, POs) */}
                    {(checkUserRights().canSeeSplrs || (!checkUserRights().canSeeCstrs && !checkUserRights().canSeeSplrs)) && (
                        <>
                            {/* Purchase Card */}
                            <DashboardCard
                                title="Purchases"
                                icon={Banknote}
                                iconCol="text-sahi-orange"
                                amount={quickStatsTxn?.purchaseInvoice?.amount}
                                percentage={quickStatsTxn?.purchaseInvoice?.percentageChange}
                                xid={"2"}
                            />

                            {/* Payments Card */}
                            <DashboardCard
                                title="Payments"
                                icon={CircleDollarSignIcon}
                                iconCol="text-sahi-orange"
                                amount={quickStatsTxn?.payment?.amount}
                                percentage={quickStatsTxn?.payment?.percentageChange}
                                xid={"6"}
                            />

                            {/* POs Card - Conditionally rendered based on showPOs prop */}
                            {showPOs && (
                                <DashboardCard
                                    title="POs"
                                    icon={BsCartFill}
                                    iconCol="text-sahi-orange"
                                    amount={quickStatsOrders?.purchaseOrder?.amount}
                                    percentage={quickStatsOrders?.purchaseOrder?.percentageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            <TransactionEntry
                isOpen={openTransactionEntry}
                onClose={() => setOpenTransactionEntry(false)}
                transactionTypeID={transactionTypeID}
            />
        </div>
    );
};

export default QuickStats;
